import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { LineReveal } from "@/components/Motion";

const HERO_IMG =
  "https://images.pexels.com/photos/5789190/pexels-photo-5789190.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const goDonate = (e) => {
    e.preventDefault();
    document.querySelector("#donate")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen pt-28 md:pt-32 pb-16 px-6 md:px-10 max-w-[1400px] mx-auto"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-xs uppercase tracking-[0.3em] text-[#C85A3C] mb-8"
            data-testid="hero-overline"
          >
            A New Jersey Nonprofit · Keeping Families Together
          </motion.p>

          <h1 className="font-serif-display font-light leading-[0.95] tracking-tight text-[#1A1A1A] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl">
            <LineReveal
              testid="hero-title"
              delay={0.35}
              lines={["Housing, dignity,", "and support for"]}
            />
            <span className="block overflow-hidden">
              <motion.span
                className="block italic text-[#C85A3C]"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.35 + 2 * 0.12, ease: [0.22, 1, 0.36, 1] }}
              >
                mothers &amp; children.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#1A1A1A]/70"
            data-testid="hero-subtitle"
          >
            Independence Way stands beside women with intellectual and mental
            health disabilities — offering rental assistance and supportive services
            that help vulnerable families stay together and build independence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#donate"
              onClick={goDonate}
              data-testid="hero-donate-btn"
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F4F1EA] rounded-full px-7 py-4 text-sm uppercase tracking-[0.15em] hover:bg-[#C85A3C] transition-colors duration-300"
            >
              Support a Family
              <ArrowDownRight size={17} />
            </a>
            <a
              href="#mission"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#mission")?.scrollIntoView({ behavior: "smooth" });
              }}
              data-testid="hero-learn-btn"
              className="link-underline text-sm uppercase tracking-[0.15em] text-[#1A1A1A]/70"
            >
              Our Mission
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-t-[140px] rounded-b-2xl aspect-[3/4] bg-[#8A9A86]/20"
          >
            <motion.img
              src={HERO_IMG}
              alt="A mother holding her child in warm sunlight"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-[120%] object-cover object-center"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-[#C85A3C] mix-blend-multiply opacity-[0.12]" aria-hidden="true" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
